import { useEffect, useState, useMemo } from "react";
import styled from "styled-components/macro";
import HorizontalBarstack from "./HorizontalBarstack";
import { MachineBreakdownRowMetrics } from "./Metrics";
import { ProcessNameLink } from "./ProcessNameLink";
import type { Process, RollupWithTagsAndCountsAndRange } from "types/types";
import { Button, Badge } from "@mui/material";
import { PnlSummaryChartContent } from "components/Dashboard/PnlSummaryChart/PnlSummaryChartContent/PnlSummaryChartContent";
import {
  useFocusRange,
  useSelectedProcesses,
} from "utils/customHooks/genericHooks";
import { usePnlSummaryChartSectionsForProcess } from "utils/customHooks/pnlChartHooks";
import useContainerDimensions from "utils/useContainerDimensions";
import {
  BREAKPOINT_MOBILE,
  BREAKPOINT_TABLET,
  CHART_MAX_WIDTH,
} from "constants/constants";

// Define mobile breakpoint for chart interactions
const MOBILE_CHART_INTERACTION_BREAKPOINT = 506;
import {
  darkModeTitleBackgroundColor,
  darkModeModalBackgroundColor,
  lightestGrey,
  lightGrey,
  medGrey,
  lightAccentSuccess90,
  lightAccentSuccess10,
  lightAccentSuccess50,
} from "constants/colors";
import CallIndicatorMachineBreakdownRow from "./CallIndicatorMachineBreakdownRow";
import { useIsDarkMode } from "store/atoms";
import {
  LINE_ROLE_ICONS,
  useLeaderConstraintProcessIds,
} from "components/Dashboard/Tiles/ProcessTile/LineRoleDecorator";
import { useSelector } from "store/useSelector";
import { addShiftlyRollupsIntoSingleRollup } from "../../TrendChart/trendChartUtils";
import { calcPnlChartCategories } from "../../PnlSummaryChart/calcPnlChartCategories";
import { TopThreeLossesChart } from "./TopThreeLossesChart";

/** a single row with machineName - metrics - tag breakdown horizontal barstack,
 * which can expand to reveal a PnlSummaryChart for the process
 * In expanded state, shows metrics row and top-3-losses chart
 * When row is clicked in expanded state, shows full PnlSummaryChart and hides top-3-losses
 */
export function MachineBreakdownRowCollapsible({
  process,
  processIdx,
  showMetrics,
  showMetricsViewInsteadOfTagBreakdown,
  rollupDisplay,
  open,
  setExpandedRow,
  isTagSelected,
  selectedTag,
  isShiftSelected,
  selectedShift,
}: {
  process: Process;
  processIdx: number;
  showMetrics: boolean;
  showMetricsViewInsteadOfTagBreakdown: boolean;
  rollupDisplay: RollupWithTagsAndCountsAndRange;
  open: boolean;
  setExpandedRow: React.Dispatch<React.SetStateAction<number | null>>;
  isTagSelected?: boolean;
  selectedTag?: string | null;
  isShiftSelected?: boolean;
  selectedShift?: string | null;
}) {
  const [isPnlChartExpanded, setIsPnlChartExpanded] = useState(false);
  // Track if the row is in closing animation state
  const [isClosing, setIsClosing] = useState(false);

  const [ref, dimensions] = useContainerDimensions();
  const [shouldRenderChart, setShouldRenderChart] = useState(false);

  // Check if we're on mobile based on container width
  const isMobileWidth = dimensions.width
    ? dimensions.width < MOBILE_CHART_INTERACTION_BREAKPOINT
    : false;

  // Immediately mount/unmount charts with no animation delays
  useEffect(() => {
    if (open && !shouldRenderChart) {
      // Immediately render chart when opened
      setShouldRenderChart(true);
    } else if (!open && shouldRenderChart) {
      // Immediately unmount and reset when closed
      setShouldRenderChart(false);
      setIsPnlChartExpanded(false);
    }
  }, [open, shouldRenderChart]);

  // Toggle the row expansion when clicked
  const togglePnlChart = () => {
    if (!isTagSelected) {
      if (!open) {
        setExpandedRow(processIdx);
        // Reset closing state when opening
        setIsClosing(false);
      } else if (!isPnlChartExpanded) {
        setIsPnlChartExpanded(true);
      } else {
        // Start closing animation
        setIsClosing(true);
        setTimeout(() => {
          setExpandedRow(null);
          setIsPnlChartExpanded(false);
          setIsClosing(false);
        }, 400); // Match the transition duration
      }
    }
  };

  const selectedProcessIdsRef = useSelectedProcesses();

  const [isDarkMode] = useIsDarkMode();

  const { leaderProcessId, constraintProcessIds } =
    useLeaderConstraintProcessIds();

  const isLeader = leaderProcessId === process.id;
  const isConstraint = constraintProcessIds?.includes(process.id);

  return (
    <MachineBreakdownRowCollapsibleStyles
      ref={ref}
      className="horizontalBarstackWrapper"
      $isSelected={selectedProcessIdsRef.current.includes(process.id)}
      $open={open}
      $isDarkMode={isDarkMode}
      $isPnlChartExpanded={isPnlChartExpanded}
      onClick={togglePnlChart}
    >
      <Button
        className="btnMachineBreakdownRow"
        disabled={isTagSelected}
        onClick={e => {
          // Stop propagation to prevent double firing with the parent onClick
          e.stopPropagation();
          togglePnlChart();
        }}
      >
        <CallIndicatorMachineBreakdownRow {...{ process }} />
        <Badge
          badgeContent={
            isLeader
              ? LINE_ROLE_ICONS.LEADER
              : isConstraint
                ? LINE_ROLE_ICONS.CONSTRAINT
                : null
          }
          anchorOrigin={{ horizontal: "left", vertical: "top" }}
        >
          <ProcessNameLink
            {...{
              process,
              processIdx,
            }}
          />
        </Badge>
        <div className="spacer" />
        {!isTagSelected &&
          (showMetrics || showMetricsViewInsteadOfTagBreakdown) && (
            <>
              <MachineBreakdownRowMetrics
                {...{
                  process,
                  isShiftSelected,
                  selectedShift,
                }}
              />
              <div className="spacer" />
            </>
          )}
        {!isTagSelected && !showMetricsViewInsteadOfTagBreakdown ? (
          <HorizontalBarstack {...{ rollupDisplay, process }} />
        ) : null}
      </Button>
      {/* Always show metrics in expanded state if not already shown */}
      {(open || isClosing) &&
        !showMetrics &&
        !showMetricsViewInsteadOfTagBreakdown && (
          <div
            className={`expandedMetricsContainer ${!isClosing ? "open" : "closing"}`}
            onClick={() => togglePnlChart()}
          >
            <MachineBreakdownRowMetrics
              {...{
                process,
                isShiftSelected,
                selectedShift,
                showHeaders: true, // Show metric names when expanded
              }}
            />
          </div>
        )}

      {/* Shared container for both charts with smooth transition */}
      {/* Using conditional rendering for initial mounting, but keeping element in DOM while animating out */}
      {(open || isClosing) && (
        <div className={`chartsContainer ${!isClosing ? "open" : "closing"}`}>
          {/* Top 3 Losses Chart */}
          <div
            className={`chartWrapper topLossesWrapper ${isPnlChartExpanded ? "hidden" : "visible"} ${isMobileWidth ? "mobile-interaction" : ""}`}
            onClick={e => {
              // On mobile width, don't allow chart clicks to toggle
              if (!isMobileWidth && !isPnlChartExpanded) {
                togglePnlChart();
              } else {
                // On mobile, stop propagation to prevent parent row from toggling
                if (isMobileWidth) {
                  e.stopPropagation();
                }
              }
            }}
          >
            <TopThreeLossesChart
              process={process}
              isTagSelected={isTagSelected}
              selectedTag={selectedTag}
              isShiftSelected={isShiftSelected}
              selectedShift={selectedShift}
              visible={!isPnlChartExpanded}
            />
          </div>

          {/* Full PNL Chart */}
          <div
            className={`chartWrapper pnlChartWrapper ${isPnlChartExpanded ? "visible" : "hidden"} ${isMobileWidth ? "mobile-interaction" : ""}`}
            onClick={e => {
              // On mobile width, don't allow chart clicks to toggle
              if (!isMobileWidth && isPnlChartExpanded) {
                togglePnlChart();
              } else {
                // On mobile, stop propagation to prevent parent row from toggling
                if (isMobileWidth) {
                  e.stopPropagation();
                }
              }
            }}
          >
            {shouldRenderChart && isPnlChartExpanded ? (
              <PnlSummaryChartForMachine
                {...{
                  process,
                  width: Math.min(CHART_MAX_WIDTH, dimensions.width || 0),
                  isTagSelected,
                  selectedTag,
                  isShiftSelected,
                  selectedShift,
                  disableAnimation: true, // Disable all animations for instant appearance
                }}
              />
            ) : null}
          </div>
        </div>
      )}
    </MachineBreakdownRowCollapsibleStyles>
  );
}

const MachineBreakdownRowCollapsibleStyles = styled.div<{
  $isSelected: boolean;
  $open: boolean;
  $isDarkMode: boolean;
  $isPnlChartExpanded?: boolean;
}>`
  font-family: "Roboto";
  transition: background 0.2s ease-in-out;
  background: ${({ $open, $isSelected, $isDarkMode }) =>
    $open
      ? $isDarkMode
        ? darkModeModalBackgroundColor
        : lightestGrey
      : !$isSelected
        ? medGrey
        : "none"};
  cursor: pointer;
  position: relative;
  border-radius: 4px;
  &:hover {
    background: ${({ $isDarkMode }) =>
      $isDarkMode ? darkModeModalBackgroundColor : "#efefef"};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    transition: all 0.15s ease-in-out;
  }
  /* darken PnlChart header rows (which are grey on tablet+) since background is darkened */
  @media (min-width: ${BREAKPOINT_TABLET}px) {
    [class*="HeaderRowCellStyles"],
    [class*="HeaderRowCellStyles"]:after {
      background: ${({ $isDarkMode }) =>
        $isDarkMode ? darkModeTitleBackgroundColor : lightGrey};
    }
  }
  .btnMachineBreakdownRow {
    height: 60px;
    padding: 16px 4px;
    @media (min-width: ${BREAKPOINT_MOBILE}px) {
      padding: 16px 24px;
    }
    display: flex;
    width: 100%;
    box-sizing: border-box;
    align-items: center;
    justify-content: flex-start;
    background: transparent;
    cursor: pointer;

    &:hover {
      background: transparent;
    }
  }
  .MuiBadge-badge {
    top: 8px;
    left: -14px;
    svg {
      transform: scale(0.8);
      color: ${lightAccentSuccess90};
      background: ${lightAccentSuccess10};
      border: 1px solid ${lightAccentSuccess50};
      border-radius: 4px;
    }
    @media (max-width: ${BREAKPOINT_MOBILE}px) {
      top: 6px;
      left: -10px;
      svg {
        transform: scale(0.6);
      }
    }
  }

  .expandedMetricsContainer {
    padding: 12px 24px 0;
    display: flex;
    align-items: center;
    pointer-events: auto; /* Enable clicks on metrics */
    opacity: 0; /* Start with opacity 0 */
    will-change: opacity; /* Optimize for opacity animations */
  }

  .expandedMetricsContainer.open {
    opacity: 1;
    transition: opacity 0.4s ease-in-out;
    animation: fadeIn 0.4s ease-in-out; /* Apply fade-in animation */
  }

  .expandedMetricsContainer.closing {
    opacity: 0;
    transition: opacity 0.4s ease-in-out;
  }

  /* Shared container for both charts */
  .chartsContainer {
    position: relative;
    pointer-events: none; /* Allow clicks to pass through to parent */
    /* No height transition - instant appearance */
    overflow: visible;
    opacity: 0; /* Start with opacity 0 */
    will-change: opacity; /* Optimize for opacity animations */
  }

  .chartsContainer.open {
    opacity: 1;
    transition: opacity 0.4s ease-in-out;
  }

  .chartsContainer.closing {
    opacity: 0;
    transition: opacity 0.4s ease-in-out;
  }

  /* Apply initial animation on mount */
  .chartsContainer .topLossesWrapper {
    animation: fadeIn 0.4s ease-in-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  /* Wrapper for each chart type */
  .chartWrapper {
    position: relative;
    transition: opacity 0.3s ease-in-out;
    pointer-events: none; /* Allow clicks to pass through to parent by default */
  }

  /* Mobile-specific interaction styles - allow direct interaction with chart elements */
  .chartWrapper.mobile-interaction {
    pointer-events: auto;
    cursor: default;
  }

  .chartWrapper.mobile-interaction:hover {
    background: transparent; /* Prevent any hover effects on mobile */
  }

  /* Visibility states for charts */
  .chartWrapper.visible {
    opacity: 1;
    z-index: 2;
  }

  .chartWrapper.hidden {
    opacity: 0;
    z-index: 1;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
  }

  /* Style for PnlSummaryChartContent to make it pass through clicks */
  .MuiCollapse-root {
    pointer-events: none;
  }
`;

function PnlSummaryChartForMachine({
  process,
  width,
  isTagSelected,
  selectedTag,
  isShiftSelected,
  selectedShift,
  disableAnimation,
}: {
  process: Process;
  width?: number;
  isTagSelected?: boolean;
  selectedTag?: string | null;
  isShiftSelected?: boolean;
  selectedShift?: string | null;
  disableAnimation?: boolean;
}) {
  const focusRange = useFocusRange();

  // Get all rollups data for shift filtering
  const allRollupsMetricTrendChartBarsArrays = useSelector(
    state => state.legacy.allRollupsMetricTrendChartBarsArrays
  );

  // Get hidden PNL chart categories
  const hiddenPnlChartCategories = useSelector(
    state => state.legacy.hiddenPnlChartCategories
  );

  // Get the standard PNL sections for reference and reuse when not filtering
  const pnlSummaryChartSectionsForProcess =
    usePnlSummaryChartSectionsForProcess(process.id);

  // Generate PNL chart sections based on filter state
  const pnlChartSections = useMemo(() => {
    // If shift is selected, create shift-filtered rollup
    if (isShiftSelected && selectedShift && focusRange) {
      // Get rollups for this process
      const rollupsForProcess =
        allRollupsMetricTrendChartBarsArrays[process.id];

      if (!rollupsForProcess || rollupsForProcess.length === 0) {
        return [];
      }

      // Filter rollups for just this shift
      const rollupsForShift = rollupsForProcess.filter(
        ({ shift }) => shift?.name === selectedShift
      );

      if (rollupsForShift.length === 0) {
        return [];
      }

      try {
        // Add all the rollups of that shift together over the focus range
        const summedShiftlyRollup = addShiftlyRollupsIntoSingleRollup({
          rollupsArr: rollupsForShift,
          focusRange,
          shiftName: selectedShift,
        });

        // Calculate PNL chart categories from the shift-summed rollup
        // This matches what's done in filterPnlChartsForTrendsPage
        return calcPnlChartCategories(
          summedShiftlyRollup,
          hiddenPnlChartCategories
        );
      } catch (e) {
        console.error("Error creating shift-filtered PNL chart", e);
        return [];
      }
    }

    // If no shift selected, use standard PNL chart data
    return pnlSummaryChartSectionsForProcess;
  }, [
    isShiftSelected,
    selectedShift,
    focusRange,
    process.id,
    allRollupsMetricTrendChartBarsArrays,
    hiddenPnlChartCategories,
    pnlSummaryChartSectionsForProcess,
  ]);

  // Apply tag filtering if needed
  const filteredSections = useMemo(() => {
    if (!pnlChartSections || pnlChartSections.length === 0) {
      return [];
    }

    // If tag filtering is applied, only show bars for the selected tag
    if (isTagSelected && selectedTag) {
      return pnlChartSections
        .map(section => ({
          ...section,
          bars: section.bars.filter(bar => bar.title === selectedTag),
          // Important: recalculate section time and percent based on filtered bars
          time: section.bars
            .filter(bar => bar.title === selectedTag)
            .reduce((sum, bar) => sum + (bar.time || 0), 0),
          percent: section.bars
            .filter(bar => bar.title === selectedTag)
            .reduce((sum, bar) => sum + (bar.percent || 0), 0),
        }))
        .filter(section => section.bars.length > 0);
    }

    return pnlChartSections;
  }, [isTagSelected, selectedTag, pnlChartSections]);

  // Only render if we have focus range and sections
  return (
    focusRange && (
      <PnlSummaryChartContent
        pnlBreakdownChartCategorySectionsArr={filteredSections}
        isInsideMachineBreakdownChart={true}
        timeRange={focusRange}
        disableAnimation={disableAnimation}
      />
    )
  );
}
